import { useEffect, useState } from 'react'
import { api } from '../services/api'

export interface Features {
  readonly demo: boolean
  readonly central: boolean
  readonly ehr: boolean
  readonly external_api: boolean
  readonly qc: boolean
  readonly remote_access: boolean
  readonly data_sharing_opt_out: boolean
  readonly offsite_backup: boolean
  readonly hipaa: boolean
  readonly fax_ingest?: boolean
  readonly migration?: boolean
  // §7.6.15: label printing enabled for all V1 deployments by default;
  // no server-side license flag wired yet — server always returns true.
  readonly labelPrinting: boolean
  // Deployment capability (not a license flag): host container runtime present.
  // False on Fargate → hides the Containers nav/page (ADR 0015). Optional so a
  // cached older API response (no field) defaults to hidden via `?? false`.
  readonly hostContainerAdmin?: boolean
}

const DEFAULT_FEATURES: Features = {
  demo: false,
  central: false,
  ehr: false,
  external_api: false,
  qc: false,
  remote_access: false,
  data_sharing_opt_out: false,
  offsite_backup: false,
  hipaa: false,
  labelPrinting: true,
}

// Module-scoped cache: shared by every caller. Invalidated on login/logout
// so the hook re-fetches per-org features for the authenticated user.
let cached: Features | null = null
let inflight: Promise<Features> | null = null

// Listeners registered by mounted useFeatures() instances. Called by
// invalidateFeatures() so each instance triggers a re-fetch.
const listeners = new Set<() => void>()

async function load(): Promise<Features> {
  if (cached) return cached
  if (inflight) return inflight
  inflight = api
    .get<Features>('/meta/features')
    .then((res) => {
      cached = res.data ?? DEFAULT_FEATURES
      return cached
    })
    .catch(() => DEFAULT_FEATURES)
    .finally(() => {
      inflight = null
    })
  return inflight
}

/**
 * Clears the features cache and notifies all mounted useFeatures() instances
 * to re-fetch. Call after login or logout so per-org license flags update.
 */
export function invalidateFeatures(): void {
  cached = null
  inflight = null
  listeners.forEach((fn) => fn())
}

/**
 * Returns the current deployment's feature flags. During the initial fetch
 * the hook returns `DEFAULT_FEATURES` (everything off), which is the safe
 * default for UI gating -- demo-only UI stays hidden until we know otherwise.
 *
 * Re-fetches automatically when invalidateFeatures() is called (e.g. after
 * login or logout) so per-org license flags are reflected immediately.
 */
export function useFeatures(): Features {
  const [features, setFeatures] = useState<Features>(cached ?? DEFAULT_FEATURES)
  const [tick, setTick] = useState(0)

  // Subscribe to invalidation signals from the module-scoped listeners set.
  // This is intentional external-system synchronization — the canonical use
  // case for useEffect. The listener bumps tick, which triggers the fetch
  // effect below via its dependency.
  useEffect(() => {
    const notify = () => setTick((t) => t + 1)
    listeners.add(notify)
    return () => {
      listeners.delete(notify)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    load().then((f) => {
      if (!cancelled) setFeatures(f)
    })
    return () => {
      cancelled = true
    }
  }, [tick])

  return features
}
